import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import type { StreamContextType } from './Types';
import { streamLog } from './streamLogger';
import { getStreamUIState } from './streamStore';

type StreamContextValue = {
  streamContext: StreamContextType | null;
  setStreamContext: (ctx: StreamContextType | null) => void;
};

const StreamProviderContext = createContext<StreamContextValue | undefined>(undefined);

// Global live context reference for non-React modules (e.g., CodeAI) to read
let latestStreamContext: StreamContextType | null = null;
export function getLatestStreamContext(): StreamContextType | null {
  return latestStreamContext;
}

export const StreamContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [streamContext, setStreamContext] = useState<StreamContextType | null>(null);
  const value = useMemo(() => ({ streamContext, setStreamContext }), [streamContext]);
  
  // Keep global reference in sync for consumers outside React tree
  useEffect(() => {
    latestStreamContext = streamContext;
    try {
      const uiState = getStreamUIState();
      const length = uiState?.content?.totalLength ?? -1;
      const isStreaming = uiState?.isStreaming ?? false;
      streamLog.ctx('[StreamContext] latestStreamContext set', { present: !!streamContext, totalLength: length, isStreaming });
    } catch (_) {}
  }, [streamContext]);

  // On unmount, clear the global reference to avoid stale context reuse
  useEffect(() => {
    return () => {
      latestStreamContext = null;
      try { streamLog.ctx('[StreamContext] latestStreamContext cleared on unmount'); } catch (_) {}
    };
  }, []);

  return <StreamProviderContext.Provider value={value}>{children}</StreamProviderContext.Provider>;
};

export function useStreamContext(): StreamContextValue {
  const ctx = useContext(StreamProviderContext);
  if (!ctx) {
    return { streamContext: null, setStreamContext: () => {} };
  }
  return ctx;
}
